/**
 * Paddle checkout wrapper.
 *
 * Flow:
 *   1. User clicks Buy → openCheckout(product) opens Paddle overlay
 *   2. Paddle collects email + payment in its overlay
 *   3. Paddle processes payment and fires signed webhook to our server
 *   4. Webhook (with include_sensitive_fields=true) carries customer email
 *      directly in the payload — no client-side capture needed
 *   5. Paddle redirects the user to /platform/welcome?txn={transaction_id}
 *
 * Env vars:
 *   NEXT_PUBLIC_PADDLE_CLIENT_TOKEN
 *   NEXT_PUBLIC_PADDLE_ENVIRONMENT          ('production' | 'sandbox')
 *   NEXT_PUBLIC_PADDLE_PACK_SINGLE_ID
 *   NEXT_PUBLIC_PADDLE_PACK_STARTER_ID
 *   NEXT_PUBLIC_PADDLE_PACK_POWER_ID
 *   NEXT_PUBLIC_PADDLE_PRO_RETAINER_ID
 */

import {
  initializePaddle,
  CheckoutEventNames,
  type Paddle,
} from '@paddle/paddle-js'
import {
  radarAnalytics,
  type CheckoutEntryPoint,
} from '@/lib/analytics/radar-events'
import { peekVisitorId } from '@/lib/analytics/visitor-id'

export type PaddleProduct =
  | 'pack-single'
  | 'pack-starter'
  | 'pack-power'
  | 'retainer'

/**
 * Server-authorized checkout material for an organization-scoped Sites plan.
 *
 * The browser never chooses the organization, plan, or price independently:
 * `/api/sites/workspaces/[orgId]/checkout` returns this exact tuple after it
 * verifies the current owner and signs a short-lived checkout grant.
 */
export interface SitesPaddleCheckout {
  priceId: string
  checkoutGrant: string
  successUrl: string
}

export class SitesCheckoutUnavailableError extends Error {
  readonly name = 'SitesCheckoutUnavailableError'

  constructor() {
    super('Checkout is temporarily unavailable. Please try again shortly.')
  }
}

const SITES_WORKSPACE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function parseSitesCheckoutSuccessUrl(
  value: unknown,
  orgId: unknown,
  origin: unknown
): URL | null {
  if (
    typeof value !== 'string' ||
    typeof orgId !== 'string' ||
    typeof origin !== 'string'
  ) {
    return null
  }

  const workspaceId = orgId.trim().toLowerCase()
  if (!SITES_WORKSPACE_ID.test(workspaceId)) return null

  try {
    const expectedOrigin = new URL(origin).origin
    const successUrl = new URL(value, expectedOrigin)
    if (
      successUrl.origin !== expectedOrigin ||
      successUrl.username ||
      successUrl.password ||
      successUrl.pathname !== `/sites/workspaces/${workspaceId}` ||
      successUrl.search !== '?checkout=success' ||
      successUrl.hash
    ) {
      return null
    }
    return successUrl
  } catch {
    return null
  }
}

/** Runtime guard for the server-authorized, workspace-bound checkout return. */
export function isSitesCheckoutSuccessUrl(
  value: unknown,
  orgId: unknown,
  origin: unknown
): boolean {
  return parseSitesCheckoutSuccessUrl(value, orgId, origin) !== null
}

const PADDLE_PRICE_IDS: Record<PaddleProduct, string> = {
  'pack-single': process.env.NEXT_PUBLIC_PADDLE_PACK_SINGLE_ID || '',
  'pack-starter': process.env.NEXT_PUBLIC_PADDLE_PACK_STARTER_ID || '',
  'pack-power': process.env.NEXT_PUBLIC_PADDLE_PACK_POWER_ID || '',
  retainer: process.env.NEXT_PUBLIC_PADDLE_PRO_RETAINER_ID || '',
}

let paddlePromise: Promise<Paddle | undefined> | null = null

function getPaddle(): Promise<Paddle | undefined> {
  if (typeof window === 'undefined') {
    return Promise.resolve(undefined)
  }

  if (paddlePromise) return paddlePromise

  const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN
  if (!token) {
    console.error(
      '[Paddle] NEXT_PUBLIC_PADDLE_CLIENT_TOKEN is not set. Checkout will not open.'
    )
    return Promise.resolve(undefined)
  }

  const environment =
    (process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT as
      | 'production'
      | 'sandbox'
      | undefined) || 'production'

  paddlePromise = initializePaddle({
    environment,
    token,
    // Passive analytics only — never alters checkout behavior. No payload
    // fields are read: Paddle event data carries the customer email, which
    // must never reach client analytics.
    eventCallback: event => {
      try {
        if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
          radarAnalytics.checkoutCompleted()
        } else if (
          event.name === CheckoutEventNames.CHECKOUT_ERROR ||
          event.name === CheckoutEventNames.CHECKOUT_FAILED
        ) {
          radarAnalytics.checkoutFailed()
        }
      } catch {
        // Analytics must never break checkout.
      }
    },
  })
  return paddlePromise
}

export function openCheckout(
  product: PaddleProduct,
  entryPoint: CheckoutEntryPoint,
  extraData?: Record<string, string>
): Promise<void> {
  return openCheckoutInternal(product, entryPoint, extraData).catch(() => {
    console.error('[Paddle] Checkout failed to open.')
  })
}

/**
 * Open Paddle for an organization-scoped Radar Sites subscription.
 *
 * Unlike the legacy Radar helper above, failures reject so the Sites billing
 * surface can show an actionable error. Only the opaque signed grant reaches
 * Paddle custom data; raw org/user/plan values supplied by the browser are
 * never trusted by fulfillment.
 */
export async function openSitesCheckout(
  checkout: SitesPaddleCheckout,
  orgId: string
): Promise<void> {
  if (typeof window === 'undefined') {
    throw new SitesCheckoutUnavailableError()
  }

  const priceId = checkout?.priceId?.trim()
  const checkoutGrant = checkout?.checkoutGrant?.trim()
  if (
    !priceId ||
    priceId.length > 255 ||
    !/^[A-Za-z0-9_-]+$/.test(priceId) ||
    !checkoutGrant ||
    checkoutGrant.length > 4096 ||
    /\s/.test(checkoutGrant)
  ) {
    throw new SitesCheckoutUnavailableError()
  }

  const successUrl = parseSitesCheckoutSuccessUrl(
    checkout?.successUrl,
    orgId,
    window.location.origin
  )
  if (!successUrl) {
    throw new SitesCheckoutUnavailableError()
  }

  const paddle = await getPaddle()
  if (!paddle) throw new SitesCheckoutUnavailableError()

  try {
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      settings: { successUrl: successUrl.toString() },
      customData: {
        source: 'radar_sites',
        sitesGrant: checkoutGrant,
      },
    })
  } catch {
    throw new SitesCheckoutUnavailableError()
  }
}

async function openCheckoutInternal(
  product: PaddleProduct,
  entryPoint: CheckoutEntryPoint,
  extraData?: Record<string, string>
): Promise<void> {
  if (typeof window === 'undefined') return

  const priceId = PADDLE_PRICE_IDS[product]
  if (!priceId) {
    console.error(
      `[Paddle] Price ID not configured for product "${product}". Set NEXT_PUBLIC_PADDLE_*_ID env var.`
    )
    return
  }

  const paddle = await getPaddle()
  if (!paddle) {
    console.error('[Paddle] SDK failed to initialize.')
    return
  }

  // Where Paddle redirects after payment:
  //   - platform_unlock ($5 from the /platform 13-tool teaser) → the buyer's
  //     own result page (/platform/check/<id>), which finalizes the unlock and
  //     links on to the dashboard. The magic-link email also signs them into the
  //     dashboard, which auto-runs the full 13-tool audit for the domain.
  //   - tool_unlock ($5 from a /tools/* teaser) → the /tools unlock landing.
  //   - everything else → welcome.
  // For a pay-first /tools run, mint a browser-side run id and carry it BOTH in
  // customData (the signed webhook records the paid run under it) and in the
  // success URL as a literal. Paddle does not reliably echo the transaction id
  // back to the browser, so we control the reference end to end; the webhook is
  // the source of truth for payment.
  const toolRunId =
    extraData?.source === 'tool_run' ? crypto.randomUUID() : null

  let successUrl: string
  if (extraData?.source === 'tool_run') {
    // The $5 buys exactly ONE run of ONE specific tool. /tools/run polls the
    // run-paid endpoint, which runs that one tool once and renders in the tool
    // flow — never the generic dashboard, no credit granted.
    successUrl = `${window.location.origin}/tools/run?rid=${toolRunId}`
  } else if (extraData?.source === 'platform_unlock' && extraData?.reportId) {
    // The buyer's own result page. The webhook flips it unlocked (full report)
    // and grants a dashboard credit; from there they open the dashboard to run
    // the full 13-tool audit for their domain.
    successUrl = `${window.location.origin}/platform/check/${extraData.reportId}?txn={transaction_id}`
  } else if (extraData?.reportId) {
    successUrl = `${window.location.origin}/tools/unlock/${extraData.reportId}?txn={transaction_id}`
  } else {
    successUrl = `${window.location.origin}/platform/welcome?txn={transaction_id}`
  }

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    settings: { successUrl },
    customData: {
      product,
      source: 'pricing_page',
      ...extraData,
      ...(toolRunId ? { runId: toolRunId } : {}),
      // LLM referral attribution. Carried through Paddle so the SIGNED
      // transaction.completed webhook — the only trustworthy proof of payment
      // — can stamp purchased_at. Never stamped from checkout start or from
      // the unlock path, which prove intent, not payment.
      // peek, never mint: opening checkout must not create attribution for a
      // visitor who was never LLM-referred.
      ...(peekVisitorId() ? { visitorId: peekVisitorId() as string } : {}),
    },
  })

  // Emitted exactly once per checkout open, only once Checkout.open()
  // actually returned — a synchronous failure there must not count as a
  // start.
  radarAnalytics.checkoutStarted(product, entryPoint)
}
